import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Trophy, Newspaper, Shirt, Tv, Music, Gamepad2, Share2, Zap,
  ArrowRight, Sparkles, TrendingUp
} from 'lucide-react';

const CATEGORY_IMAGES = {
  sports: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/ta2xbwj2_betonsports.png",
  news: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/2uh6tdes_betonnews.png",
  fashion: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/y59jgmx6_betonfashion.png",
  entertainment: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/n4ktap1n_bet%20on%20entertainment.png",
  gaming: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/xe8vbfnq_betongaming.png",
  music: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/u7k3a1be_betonmusic.png",
  social: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/1j29pl91_betsocialtrends.png",
  wildcard: "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/mxogb0lq_betonanything.png",
};

const CATEGORIES = [
  {
    id: 'sports',
    icon: Trophy,
    title: 'Bet on Sports',
    color: '#28A745',
    description: "Take control of sports betting like never before. Don't just bet on match outcomes—create your own bets on anything happening inside the game.",
    examples: [
      "Who scores first in a local football match",
      "WWE match outcomes",
      "Total crowd attendance at a game",
      "What player will start tomorrow's game",
      "What goal celebration will the player do"
    ],
    howItWorks: "Set your own odds, challenge friends or the public, and lock in your stake.",
    cta: "Bet on Sports"
  },
  {
    id: 'news',
    icon: Newspaper,
    title: 'Bet on News',
    color: '#6F42C1',
    description: "Turn breaking news into opportunity. Predict real-world events before they happen.",
    examples: [
      "Will a certain law pass tomorrow?",
      "News anchor will wear a red top",
      "Election outcomes",
      "Major global announcements"
    ],
    howItWorks: "Create prediction-based bets and let others accept your challenge.",
    cta: "Bet on News"
  },
  {
    id: 'fashion',
    icon: Shirt,
    title: 'Bet on Fashion',
    color: '#EC4899',
    description: "Bet on trends, style, and influence. Fashion is unpredictable—now you profit from it.",
    examples: [
      "Will this sneaker sell out in 24 hours?",
      "Which outfit wins a style battle?",
      "My teacher is going to wear the same brown shoes",
      "Red carpet best-dressed challenges"
    ],
    howItWorks: "Create a bet or challenge a bet that is already set then put your money where your mouth is.",
    cta: "Bet on Fashion"
  },
  {
    id: 'entertainment',
    icon: Tv,
    title: 'Bet on Entertainment',
    color: '#D4AF37',
    description: "Movies, shows, celebrities—everything is a bet.",
    examples: [
      "Will a movie hit #1 this weekend?",
      "Who wins an award?",
      "Will a series get renewed?",
      "Celebrity drama outcomes"
    ],
    howItWorks: "Create bets around releases, ratings, and public reactions.",
    cta: "Bet on Entertainment"
  },
  {
    id: 'music',
    icon: Music,
    title: 'Bet on Music',
    color: '#FF6B6B',
    description: "From viral hits to global charts—predict the next big sound.",
    examples: [
      "Will a song reach 1M streams in 24 hours?",
      "Which artist drops first?",
      "Battle: Artist vs Artist",
      "Album chart rankings"
    ],
    howItWorks: "Set conditions and let fans take the other side.",
    cta: "Bet on Music"
  },
  {
    id: 'gaming',
    icon: Gamepad2,
    title: 'Bet on Gaming',
    color: '#17A2B8',
    description: "Bet on real gameplay, live challenges, and online competitions.",
    examples: [
      "1v1 matches (FIFA, COD, Fortnite)",
      "Tournament winners",
      "Skill challenges (fastest time, highest score)",
      "Live streaming bets"
    ],
    howItWorks: "Play, stream, or challenge others directly and stake your bet.",
    cta: "Bet on Gaming"
  },
  {
    id: 'social',
    icon: Share2,
    title: 'Bet on Social Trends',
    color: '#8B5CF6',
    description: "If it can go viral, it can be a bet.",
    examples: [
      "Will this video hit 100K likes?",
      "Which post performs better?",
      "Influencer growth predictions",
      "Viral trend challenges"
    ],
    howItWorks: "Create bets based on engagement metrics and timelines.",
    cta: "Bet on Social"
  },
  {
    id: 'wildcard',
    icon: Zap,
    title: 'Bet on Anything Worth The BET',
    color: '#DC3545',
    description: "Turn your confidence into cash. Challenge anyone, anywhere.",
    examples: [
      "I bet my boyfriend/girlfriend is going to be late",
      "Tomorrow is my birthday I'm getting an iPhone 17, want to bet?",
      "Our lights are off. I bet the electricity company puts it on by 9am",
      '"I can do this better than you" — wanna bet bro?'
    ],
    howItWorks: "Set your challenge, define the rules, stake your money, and go head-to-head.",
    cta: "Start a Challenge"
  }
];

const BetOnAnythingPage = () => {
  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
      <Header />

      {/* Hero Header */}
      <section className="py-16 px-4 text-center bg-gradient-to-b from-[#2D2D2D] to-[#1A1A1A]">
        <h1 className="text-4xl md:text-6xl font-black uppercase text-white mb-4" style={{ fontFamily: 'Outfit' }}>
          Bet on Anything? <span className="text-[#D4AF37]">Well, Technically Anything.</span>
        </h1>
        <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto">
          Create your own bets, set your own odds, challenge anyone, anywhere. 
          NO limits. No house rules. Just pure peer 2 peer betting. But <span className="text-[#D4AF37] font-bold">WAIT</span> — there is a catch. You have to bet on things that make sense and have other people match your bet, otherwise you will lose your money. <span className="text-[#D4AF37] font-bold">YOU HAVE TO BET RESPONSIBLY!</span>
        </p>
        <div className="flex justify-center gap-4 mt-8">
          <div className="stat-card">
            <TrendingUp className="h-6 w-6 mx-auto mb-2 text-[#D4AF37]" />
            <div className="text-xl font-bold text-[#D4AF37]">50+</div>
            <div className="text-xs text-gray-400">Active Bets</div>
          </div>
          <div className="stat-card">
            <Sparkles className="h-6 w-6 mx-auto mb-2 text-[#28A745]" />
            <div className="text-xl font-bold text-[#28A745]">8</div>
            <div className="text-xs text-gray-400">Categories</div>
          </div>
        </div>
      </section>

      {/* Category Banners */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto space-y-8">
          {CATEGORIES.map((category, index) => {
            const Icon = category.icon;
            const isEven = index % 2 === 0;
            return (
              <div 
                key={category.id}
                className={`card-dark overflow-hidden ${isEven ? '' : 'md:flex-row-reverse'}`}
                data-testid={`category-banner-${category.id}`}
              >
                <div className="md:flex items-stretch">
                  {/* Image/Visual Side */}
                  <div className="md:w-1/3 overflow-hidden">
                    {CATEGORY_IMAGES[category.id] ? (
                      <img 
                        src={CATEGORY_IMAGES[category.id]} 
                        alt={category.title}
                        className="w-full h-full object-cover min-h-[200px]"
                      />
                    ) : (
                      <div 
                        className="p-8 flex items-center justify-center h-full min-h-[200px]"
                        style={{ background: `linear-gradient(135deg, ${category.color}20 0%, ${category.color}05 100%)` }}
                      >
                        <div 
                          className="w-24 h-24 rounded-full flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${category.color} 0%, ${category.color}99 100%)` }}
                        >
                          <Icon className="h-12 w-12 text-white" />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Content Side */}
                  <div className="p-6 md:p-8 flex-1">
                    <h2 
                      className="text-2xl md:text-3xl font-black uppercase mb-3"
                      style={{ fontFamily: 'Outfit', color: category.color }}
                    >
                      {category.title}
                    </h2>
                    <p className="text-gray-300 mb-4">{category.description}</p>
                    
                    {/* Examples */}
                    <div className="mb-4">
                      <h4 className="text-sm font-bold text-[#D4AF37] uppercase mb-2">Example Bets:</h4>
                      <ul className="space-y-1">
                        {category.examples.map((example, idx) => (
                          <li key={idx} className="text-sm text-gray-400 flex items-start gap-2">
                            <span className="text-[#D4AF37]">•</span>
                            {example}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* How it works */}
                    <p className="text-sm text-gray-500 mb-4">
                      <span className="text-[#28A745] font-semibold">How it works:</span> {category.howItWorks}
                    </p>
                    
                    {/* CTA */}
                    <Link to="/register">
                      <Button 
                        className="btn-primary"
                        style={{ background: `linear-gradient(135deg, ${category.color} 0%, ${category.color}CC 100%)` }}
                      >
                        {category.cta} <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#D4AF37] via-[#F4D03F] to-[#D4AF37]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-black uppercase text-[#1A1A1A] mb-4" style={{ fontFamily: 'Outfit' }}>
            No Limits. No House. Just You vs The World.
          </h2>
          <p className="text-[#1A1A1A]/80 text-lg mb-8">
            Ready to back your opinions with real money?
          </p>
          <Link to="/register">
            <Button className="bg-[#1A1A1A] text-[#D4AF37] px-10 py-5 text-lg font-bold uppercase rounded-lg shadow-lg hover:bg-[#2D2D2D] transition-all">
              Create Your First Bet <ArrowRight className="ml-2 h-5 w-5 inline" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BetOnAnythingPage;
